import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { BASE_URL } from "../../config";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const exportStamp = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
    now.getDate()
  )}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
};

const formatCurrency = (value) => {
  if (value == null || value === "") return "N/A";
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    maximumFractionDigits: 2,
  }).format(parsed);
};

const formatFundManagementFlag = (value) => {
  if (value == null || value === "") return "N/A";
  return Number(value) === 1 ? "Enabled" : "Disabled";
};

const formatFundUtilPercent = (revolvingFund, remainingFund) => {
  const revolving = Number(revolvingFund);
  const remaining = Number(remainingFund);

  if (
    !Number.isFinite(revolving) ||
    revolving === 0 ||
    !Number.isFinite(remaining)
  ) {
    return "N/A";
  }

  return `${(((revolving - remaining) / revolving) * 100).toFixed(2)}%`;
};

const formatFundDate = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw || raw.startsWith("0000-00-00")) return "N/A";

  const parsed = new Date(raw.includes("T") ? raw : raw.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return raw;

  return parsed.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const StatCard = ({ icon: Icon, label, value, accent = "blue" }) => {
  const accents = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    violet: "bg-violet-50 text-violet-600",
    amber: "bg-amber-50 text-amber-600",
  };

  return (
    <div className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={clsx("rounded-lg p-2", accents[accent])}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-gray-500">{label}</div>
          <div className="truncate text-sm font-semibold text-gray-900">
            {value ?? "N/A"}
          </div>
        </div>
      </div>
    </div>
  );
};

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
  };

  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
        colors[color] || colors.gray
      )}
    >
      {children}
    </span>
  );
};

const EmptyState = ({
  title = "No results",
  subtitle = "Try adjusting your search or filters.",
}) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
    <MagnifyingGlassIcon className="mb-2 h-8 w-8 text-gray-400" />
    <div className="text-sm font-medium text-gray-900">{title}</div>
    <div className="text-xs text-gray-500">{subtitle}</div>
  </div>
);

const CorporateAddFundPage = () => {
  const { corpCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [corpName, setCorpName] = useState(location.state?.corpName || "");
  const [fundInformation, setFundInformation] = useState(null);
  const [fundInfoLoading, setFundInfoLoading] = useState(true);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPerPage, setHistoryPerPage] = useState(10);
  const [historySearch, setHistorySearch] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [remainingFund, setRemainingFund] = useState(0);
  const [fundContacts, setFundContacts] = useState([]);
  const [amount, setAmount] = useState("");
  const [bankRef, setBankRef] = useState("");
  const [remarks, setRemarks] = useState("");
  const [addFundLoading, setAddFundLoading] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const getAuthHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
    }),
    []
  );

  const loadCorporateName = useCallback(async () => {
    if (!corpCode || corpName) return;

    try {
      const response = await axios.get(`${BASE_URL}/corporates/${corpCode}`, {
        headers: getAuthHeaders(),
      });
      setCorpName(response.data?.data?.corp_name || corpCode);
    } catch (error) {
      setCorpName(corpCode);
    }
  }, [corpCode, corpName, getAuthHeaders]);

  const loadFundInformation = useCallback(async () => {
    if (!corpCode) return;

    setFundInfoLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/corporates/${corpCode}/funds`, {
        headers: getAuthHeaders(),
      });
      const fundData = response.data.data || null;
      setFundInformation(fundData);
      setRemainingFund(
        fundData?.remaining_fund ?? fundData?.remaining_fund_amt ?? 0
      );
      if (Array.isArray(fundData?.fund_contacts)) {
        setFundContacts(fundData.fund_contacts);
      }
    } catch (error) {
      setFundInformation(null);
      toast.error("Failed to fetch fund information.", { autoClose: 3000 });
    } finally {
      setFundInfoLoading(false);
    }
  }, [corpCode, getAuthHeaders]);

  const loadFundHistory = useCallback(async () => {
    if (!corpCode) return;

    setHistoryLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/corporates/${corpCode}/fund-history`,
        {
          headers: getAuthHeaders(),
          params: {
            limit_offset: (historyPage - 1) * historyPerPage,
            limit: historyPerPage,
            search_string: historySearch,
          },
        }
      );
      setHistoryRows(response.data.data || []);
      setHistoryTotal(response.data.total_count || 0);
      setRemainingFund(response.data.remaining_fund || 0);
      if (Array.isArray(response.data.fund_contacts)) {
        setFundContacts(response.data.fund_contacts);
      }
    } catch (error) {
      toast.error("Failed to fetch fund history.", { autoClose: 3000 });
    } finally {
      setHistoryLoading(false);
    }
  }, [corpCode, getAuthHeaders, historyPage, historyPerPage, historySearch]);

  useEffect(() => {
    loadCorporateName();
  }, [loadCorporateName]);

  useEffect(() => {
    loadFundInformation();
  }, [loadFundInformation]);

  useEffect(() => {
    loadFundHistory();
  }, [loadFundHistory]);

  const fundInfo = fundInformation || {};
  const revolvingFund =
    fundInfo.revolving_fund ??
    fundInfo._revolving_fund_amt ??
    fundInfo.revolving_fund_amt;
  const totalAddedFund = fundInfo.total_added_fund ?? fundInfo._corp_fund;
  const groupUtilization = fundInfo.group_utilization ?? fundInfo.total_util;
  const groupReimbursement =
    fundInfo.group_reimbursement ?? fundInfo.group_reim_util;
  const accountUtilization =
    fundInfo.account_utilization ?? fundInfo.acct_util;
  const accountReimbursement =
    fundInfo.account_reimbursement ?? fundInfo.acct_reim_util;
  const threshold =
    fundInfo.threshold ?? fundInfo._threshold_amt ?? fundInfo.threshold_amt;
  const currentRemainingFund =
    fundInfo.remaining_fund ?? fundInfo.remaining_fund_amt ?? remainingFund;
  const firstWarning =
    fundInfo.first_warning_amount ?? fundInfo._first_warning_amount;
  const totalPages = Math.max(1, Math.ceil(historyTotal / historyPerPage));

  const pageNumbers = useMemo(() => {
    const maxPagesToShow = 5;
    const startPage = Math.max(
      1,
      historyPage - Math.floor(maxPagesToShow / 2)
    );
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, index) => startPage + index
    );
  }, [historyPage, totalPages]);

  const handleAddFund = (event) => {
    event.preventDefault();

    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      toast.error("Valid amount is required.");
      return;
    }
    if (!bankRef.trim()) {
      toast.error("Bank Reference # is required.");
      return;
    }
    if (!remarks.trim()) {
      toast.error("Remarks are required.");
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmAddFund = async () => {
    setAddFundLoading(true);
    try {
      await axios.post(
        `${BASE_URL}/corporates/${corpCode}/add-fund`,
        {
          amount: parseFloat(amount).toFixed(2),
          bank_ref: bankRef,
          remarks,
          update_by: "Admin",
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Fund added successfully.");
      setShowConfirmModal(false);
      setAmount("");
      setBankRef("");
      setRemarks("");
      await Promise.all([loadFundInformation(), loadFundHistory()]);
    } catch (error) {
      toast.error(
        "Failed to add fund: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setAddFundLoading(false);
    }
  };

  const handleExportFundHistory = async (format = "csv") => {
    const normalizedFormat = format === "xlsx" ? "xlsx" : "csv";
    const setExporting =
      normalizedFormat === "xlsx" ? setExportingExcel : setExportingCsv;

    setExporting(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/corporates/${corpCode}/fund-history/export`,
        {
          headers: getAuthHeaders(),
          params: {
            search_string: historySearch,
            format: normalizedFormat,
          },
          responseType: "blob",
        }
      );

      const contentDisposition = response.headers?.["content-disposition"] || "";
      const fileNameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      const fileName = fileNameMatch
        ? fileNameMatch[1].replace(/['"]/g, "")
        : `fund_history_${corpCode}_${exportStamp()}.${normalizedFormat}`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to export fund history.", { autoClose: 3000 });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-7xl p-4">
      <ToastContainer />

      <div className="mb-4 rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-col gap-3 rounded-t-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-base font-semibold">Add Corporate Fund</h1>
            <div className="text-xs text-white/80">
              {corpName || corpCode} | Remaining Fund:{" "}
              {formatCurrency(currentRemainingFund)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/corporate")}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/20"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Corporate
          </button>
        </div>

        <div className="p-4">
          <div className="mb-6 rounded-xl border border-gray-100 bg-gray-50 p-4">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-sm font-semibold text-gray-900">
                Fund Information
              </h2>
              <Badge color={fundInfo.fund_mgmt_flag ? "green" : "gray"}>
                Fund Mgmt: {fundInfo.fund_mgmt_flag ? "Enabled" : "Disabled"}
              </Badge>
            </div>

            {fundInfoLoading ? (
              <div className="flex items-center justify-center rounded-lg border border-dashed border-gray-200 bg-white py-8">
                <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-600" />
                <span className="ml-2 text-sm text-gray-600">
                  Loading fund details...
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <StatCard
                  icon={CurrencyDollarIcon}
                  label="Revolving Fund"
                  value={formatCurrency(revolvingFund)}
                />
                <StatCard
                  icon={CurrencyDollarIcon}
                  label="Total Added Fund"
                  value={formatCurrency(totalAddedFund)}
                />
                <StatCard
                  icon={CurrencyDollarIcon}
                  label="Group Utilization"
                  value={formatCurrency(groupUtilization)}
                />
                <StatCard
                  icon={CurrencyDollarIcon}
                  label="Group Reimbursement"
                  value={formatCurrency(groupReimbursement)}
                />
                <StatCard
                  icon={CurrencyDollarIcon}
                  label="Account Utilization"
                  value={formatCurrency(accountUtilization)}
                />
                <StatCard
                  icon={CurrencyDollarIcon}
                  label="Account Reimbursement"
                  value={formatCurrency(accountReimbursement)}
                />
                <StatCard
                  icon={CurrencyDollarIcon}
                  label="Threshold"
                  value={formatCurrency(threshold)}
                />
                <StatCard
                  icon={CurrencyDollarIcon}
                  label="Remaining Fund"
                  value={formatCurrency(currentRemainingFund)}
                />
                <StatCard
                  icon={ClockIcon}
                  label="Util %"
                  value={formatFundUtilPercent(
                    revolvingFund,
                    currentRemainingFund
                  )}
                />
                <StatCard
                  icon={CurrencyDollarIcon}
                  label="1st Warning Amount"
                  value={formatCurrency(firstWarning)}
                />
              </div>
            )}
          </div>

          <form onSubmit={handleAddFund} className="mb-6 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <PlusIcon className="h-5 w-5 text-blue-600" />
              <h2 className="text-sm font-semibold text-gray-900">
                Fund Entry
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Enter Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Enter Bank Ref# *
                </label>
                <input
                  type="text"
                  value={bankRef}
                  onChange={(event) => setBankRef(event.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Enter Remarks *
                </label>
                <input
                  type="text"
                  value={remarks}
                  onChange={(event) => setRemarks(event.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  required
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                disabled={addFundLoading}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {addFundLoading ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    Adding Fund...
                  </>
                ) : (
                  <>
                    <PlusIcon className="h-5 w-5" />
                    Add Fund
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-gray-100 p-4 lg:flex-row lg:items-center lg:justify-between">
              <h2 className="text-sm font-semibold text-gray-900">
                Corporate Fund History
              </h2>
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search fund history..."
                    value={historySearch}
                    onChange={(event) => {
                      setHistorySearch(event.target.value);
                      setHistoryPage(1);
                    }}
                    className="w-full rounded-md border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:w-72"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handleExportFundHistory("csv")}
                  disabled={exportingCsv || exportingExcel}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {exportingCsv ? "Exporting CSV..." : "Export CSV"}
                </button>
                <button
                  type="button"
                  onClick={() => handleExportFundHistory("xlsx")}
                  disabled={exportingCsv || exportingExcel}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {exportingExcel ? "Exporting Excel..." : "Export Excel"}
                </button>
              </div>
            </div>

            <div className="overflow-auto">
              {historyLoading ? (
                <div className="flex items-center justify-center py-10">
                  <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-600" />
                  <span className="ml-2 text-sm text-gray-600">
                    Loading fund history...
                  </span>
                </div>
              ) : historyRows.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    title="No fund history found"
                    subtitle="No fund transactions available."
                  />
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {[
                        "Amount",
                        "Date",
                        "Added By",
                        "Bank Ref#",
                        "Remarks",
                        "Fund Name",
                        "Fund Mobile",
                        "Fund Email",
                        "Fund Mgmt",
                        "Revolving Fund",
                        "Threshold",
                      ].map((column) => (
                        <th
                          key={column}
                          className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {historyRows.map((row, index) => (
                      <tr
                        key={`${row.tbl_id || "fund"}-${index}`}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {formatCurrency(row.amount)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {formatFundDate(row.date)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {row.added_by || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {row.bank_ref || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {row.remarks || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {row.fund_name || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {row.fund_mobile || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {row.fund_email || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {formatFundManagementFlag(row.fund_mgmt_flag)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {formatCurrency(row.revolving_fund_amt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {formatCurrency(row.threshold_amt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row">
              <div className="text-xs text-gray-600">
                Showing {historyTotal ? (historyPage - 1) * historyPerPage + 1 : 0} to{" "}
                {Math.min(historyPage * historyPerPage, historyTotal)} of{" "}
                {historyTotal.toLocaleString()} entries
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  onClick={() => setHistoryPage(1)}
                  disabled={historyPage === 1}
                  className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200 disabled:text-gray-400"
                >
                  <ChevronDoubleLeftIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setHistoryPage((page) => Math.max(1, page - 1))}
                  disabled={historyPage === 1}
                  className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200 disabled:text-gray-400"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                {pageNumbers.map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setHistoryPage(page)}
                    className={clsx(
                      "rounded-md px-2 py-1 text-xs",
                      historyPage === page
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    )}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setHistoryPage((page) => Math.min(totalPages, page + 1))
                  }
                  disabled={historyPage === totalPages}
                  className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200 disabled:text-gray-400"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setHistoryPage(totalPages)}
                  disabled={historyPage === totalPages}
                  className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200 disabled:text-gray-400"
                >
                  <ChevronDoubleRightIcon className="h-5 w-5" />
                </button>
                <select
                  value={historyPerPage}
                  onChange={(event) => {
                    setHistoryPerPage(Number(event.target.value));
                    setHistoryPage(1);
                  }}
                  className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
              <h2 className="text-base font-semibold">Confirm Add Fund</h2>
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="rounded-md p-1 text-white/80 hover:bg-white/10 hover:text-white"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <p className="mb-4 text-sm text-gray-700">
                Are you sure you want to add fund to {corpName || corpCode}?
                The following contact person will be notified.
              </p>
              {fundContacts.length > 0 && (
                <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                  Contact Person:{" "}
                  {fundContacts.map((contact) => contact.fund_name).join(", ")}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={addFundLoading}
                  className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-800 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmAddFund}
                  disabled={addFundLoading}
                  className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {addFundLoading && (
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  )}
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateAddFundPage;
